import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
  Box,
  Alert,
} from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import api from "../../api";
import AppLayout from "../../components/ui/AppLayout";
import PageHeader from "../../components/ui/PageHeader";
import SectionCard from "../../components/ui/SectionCard";
import LoadingState from "../../components/ui/LoadingState";
import PlayerAvatar from "../../components/ui/PlayerAvatar";
import PartidaHistoricoCard from "../../components/PartidaHistoricoCard";
import { useAuth } from "../../AuthContext";
import tokens from "../../theme/tokens";

const medal = (index) => [tokens.color.gold, "#9CA3AF", "#B7791F"][index];

const RankingPage = () => {
  const [rankingData, setRankingData] = useState([]);
  const [minJogos, setMinJogos] = useState(0);
  const [partidas, setPartidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partidasPage, setPartidasPage] = useState(0);
  const [totalPartidas, setTotalPartidas] = useState(0);
  const [selectedDate, setSelectedDate] = useState(""); // State for the selected date
  const [openExplanation, setOpenExplanation] = useState(false);
  const partidasPerPage = 10;
  const { userLogado } = useAuth();
  const ehDiretoria = userLogado && (userLogado.perfil === "Admin" || userLogado.perfil === "Diretor");
  const [recarregar, setRecarregar] = useState(0); // muda depois de excluir uma partida
  const [aviso, setAviso] = useState(null);

  // Diretoria: exclui a partida (com os lances dela) e recarrega o histórico e o ranking
  const excluirPartida = async (partida) => {
    try {
      await api.remove(`partidas/${partida._id}`);
      setAviso({ severity: "success", texto: "Partida excluída." });
      setRecarregar((n) => n + 1);
    } catch (error) {
      setAviso({ severity: "error", texto: error.response?.data?.message || "Não foi possível excluir a partida." });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const rankingResponse = await api.get("/report/aproveitamento");
        const partidasResponse = await api.get(
          `/report/patidas?page=${partidasPage}&limit=${partidasPerPage}&date=${selectedDate}`
        );
        setRankingData(rankingResponse.data.ranking);
        setMinJogos(rankingResponse.data.minJogos);
        setPartidas(partidasResponse.data.partidas);
        setTotalPartidas(partidasResponse.data.total);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [partidasPage, selectedDate, recarregar]);

  const handleNextPage = () => {
    if ((partidasPage + 1) * partidasPerPage < totalPartidas) {
      setPartidasPage(partidasPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (partidasPage > 0) {
      setPartidasPage(partidasPage - 1);
    }
  };

  const handleOpenExplanation = () => {
    setOpenExplanation(true);
  };

  const handleCloseExplanation = () => {
    setOpenExplanation(false);
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value); // Update selectedDate with the chosen date
  };

  if (loading) {
    return <AppLayout><LoadingState label="Calculando ranking..." /></AppLayout>;
  }

  return (
    <AppLayout>
      <PageHeader
        eyebrow="Pelada"
        title="Eficiência dos jogadores"
        subtitle={`Mínimo de ${minJogos} jogos para não sofrer penalidade.`}
        actions={
          <Button variant="outlined" startIcon={<HelpOutlineRoundedIcon />} onClick={handleOpenExplanation}>
            Como é calculado
          </Button>
        }
      />
      <Dialog
        open={openExplanation}
        onClose={handleCloseExplanation}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Explicação da Fórmula de Eficiência
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Objetivo da Fórmula:</strong><br />
            A ideia por trás dessa fórmula
            é garantir que jogadores com menos jogos não fiquem à frente de
            jogadores que participaram de mais partidas apenas por terem um
            desempenho positivo em poucas partidas. Isso é feito adicionando uma
            penalidade para quem jogou menos que o minimo de jogos. (Quantidade
            de peladas * 3.) <br />
          </DialogContentText>
          <DialogContentText>
            A fórmula usada para calcular a eficiência dos jogadores leva em
            consideração o número de vitórias, empates, derrotas e uma
            penalidade para quem jogou menos do que o número mínimo de jogos
            (número de peladas * 3).
          </DialogContentText>
          <DialogContentText>
            <strong>Fórmula:</strong> <br />
            <code>
              Pontuação = (0.5 * Vitórias) + (0.25 * Empates)
              <br />
              Penalidade = (Número mínimo de jogos - Número de Jogos) * 0.1{" "}
              <br />
              Eficiência = [(Pontuação / Número de Jogos) - Penalidade] * 100
            </code>
          </DialogContentText>
          <DialogContentText>
            <strong>Exemplo 1 (sem penalidade):</strong> <br />
            Suponha que um jogador tenha jogado **9 partidas**, com 5 vitórias,
            2 empates e 2 derrotas. <br />
            A pontuação será: <br />
            <code>Pontuação = (0.5 * 5) + (0.25 * 2) = 2.5 + 0.5 = 3.0</code>
            <br />
            Como ele jogou o número mínimo de jogos (9 jogos), **não há
            penalidade**. <br />
            <strong>Eficiência:</strong>
            <br />
            <code>Eficiência = [(3.0 / 9) - 0] * 100 = 33.33%</code>
          </DialogContentText>
          <DialogContentText>
            <strong>Exemplo 2 (com penalidade):</strong> <br />
            Agora, suponha que um jogador tenha jogado **5 partidas**, com 3
            vitórias, 1 empate e 1 derrota. <br />
            A pontuação será: <br />
            <code>Pontuação = (0.5 * 3) + (0.25 * 1) = 1.5 + 0.25 = 1.75</code>
            <br />
            Como ele jogou menos que o número mínimo de jogos (9 jogos), **uma
            penalidade é aplicada**. <br />
            <strong>Penalidade:</strong> <br />
            <code>Penalidade = (9 - 5) * 0.1 = 0.4</code>
            <br />
            <strong>Eficiência:</strong> <br />
            <code>Eficiência = [(1.75 / 5) - 0.4] * 100 = 25.00%</code>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseExplanation} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

     

      <SectionCard title="Ranking" sx={{ mb: 3 }} contentSx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Jogador</TableCell>
                <TableCell align="right">Eficiência</TableCell>
                <TableCell align="center">Jogos</TableCell>
                <TableCell align="center">V</TableCell>
                <TableCell align="center">E</TableCell>
                <TableCell align="center">D</TableCell>
                <TableCell align="right">Pontos</TableCell>
                <TableCell align="right">Penalidade</TableCell>
                <TableCell align="right">Aproveitamento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankingData.map((jogador, index) => (
                <TableRow key={jogador.nome} hover>
                  <TableCell>
                    <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", fontWeight: 700, bgcolor: index < 3 ? medal(index) : "transparent", color: index < 3 ? "#fff" : "text.secondary" }}>
                      {index + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <PlayerAvatar userId={jogador.user} name={jogador.nome} size={30} />
                      <Typography variant="body2" fontWeight={600}>{jogador.nome}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{jogador.eficiencia}</TableCell>
                  <TableCell align="center">{jogador.jogos}</TableCell>
                  <TableCell align="center" sx={{ color: "success.main" }}>{jogador.vitorias}</TableCell>
                  <TableCell align="center">{jogador.empates}</TableCell>
                  <TableCell align="center" sx={{ color: "error.main" }}>{jogador.derrotas}</TableCell>
                  <TableCell align="right">{jogador.pontuacao}</TableCell>
                  <TableCell align="right">{jogador.penalidade}</TableCell>
                  <TableCell align="right">{jogador.aproveitamento}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <SectionCard
        title="Histórico de partidas"
        actions={
          <TextField
            id="date"
            size="small"
            label="Data da pelada"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />
        }
      >
        {aviso && <Alert severity={aviso.severity} onClose={() => setAviso(null)} sx={{ mb: 1.5 }}>{aviso.texto}</Alert>}
        {partidas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhuma partida encerrada nesta data.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {partidas.map((partida, indice) => (
              <PartidaHistoricoCard key={partida._id} partida={partida} numero={partidasPage * partidasPerPage + indice + 1} onExcluir={ehDiretoria ? excluirPartida : undefined} />
            ))}
          </Stack>
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button onClick={handlePreviousPage} disabled={partidasPage === 0}>Anterior</Button>
          <Button onClick={handleNextPage} disabled={(partidasPage + 1) * partidasPerPage >= totalPartidas}>Próxima</Button>
        </Stack>
      </SectionCard>
    </AppLayout>
  );
};

export default RankingPage;

